import { useState } from "react";
import type { FormEvent } from "react";
import { RagBadge, unwrap, useApi, useResource } from "@backcoach/ui";
import type { Schemas } from "@backcoach/ui";

type PlatformOrg = Schemas["PlatformOrg"];
type DseReviewItem = Schemas["DseReviewItem"];

/**
 * Platform admin's three screens (architecture page, Org management).
 * Organisations: create one, set its domains, invite its first admin,
 * pause or archive it. DSE review: the queue of checks that asked for a
 * competent-person look, one at a time, anonymised. Policies: versions and
 * who uses which. Content is not editable here; it arrives by pull request.
 */
export function OrganisationsScreen({ onOpen }: { onOpen: (org: { id: string; name: string }) => void }) {
  const api = useApi();
  const orgs = useResource(async (a) => unwrap(await a.GET("/v1/platform/orgs")));
  const [form, setForm] = useState({ name: "", domains: "", timezone: "Europe/London", headcountInScope: 0, sponsorEmail: "" });
  const [note, setNote] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    const r = await api.POST("/v1/platform/orgs", {
      body: {
        name: form.name,
        domains: form.domains.split(/[,\s]+/).filter(Boolean),
        timezone: form.timezone,
        headcountInScope: form.headcountInScope,
        sponsorEmail: form.sponsorEmail,
        workStart: "09:00",
        workEnd: "17:30",
      },
    });
    if (r.data) {
      setNote(`${r.data.name} is provisioned. ${form.sponsorEmail} can sign in by magic link.`);
      setForm({ name: "", domains: "", timezone: "Europe/London", headcountInScope: 0, sponsorEmail: "" });
      orgs.reload();
    } else {
      setNote(r.error?.message ?? "Could not create the organisation");
    }
  }

  const [pending, setPending] = useState<{ id: string; status: PlatformOrg["status"] } | null>(null);

  async function setStatus(org: PlatformOrg, status: PlatformOrg["status"]) {
    setPending(null);
    await api.PUT("/v1/platform/orgs/{orgId}/status", { params: { path: { orgId: org.id } }, body: { status } });
    orgs.reload();
  }

  // Pause, Archive and Reopen change what fifty people see, so they ask
  // once, inline, in words that say what will happen. Activate and Resume
  // are the harmless direction and go straight through.
  function confirmCopy(org: PlatformOrg, status: PlatformOrg["status"]): string {
    switch (status) {
      case "paused":
        return `Pause ${org.name}? Prompts stop within fifteen minutes. Sign-in stays open and nothing is deleted.`;
      case "archived":
        return `Archive ${org.name}? Sign-in closes and prompts stop. People keep access to their own data.`;
      default:
        return `Reopen ${org.name}? Sign-in and prompts start again.`;
    }
  }

  return (
    <div className="bc-screen bc-settings">
      <header className="bc-head">
        <p className="bc-kicker">Platform</p>
        <h1>Organisations</h1>
      </header>

      <section className="bc-block">
        <div className="bc-block-head">
          <h2>All organisations</h2>
        </div>
        {(orgs.data ?? []).length === 0 && <p className="bc-note">None yet.</p>}
        <ul className="org-list">
          {(orgs.data ?? []).map((o) => (
            <li key={o.id} className="org-row">
              <div className="org-main">
                <div className="org-title">
                  <b>{o.name}</b>
                  <span className="bc-chip bc-chip-quiet">{o.status}</span>
                </div>
                <p className="org-domains">{o.domains.join(", ")}</p>
                <p className="org-stats">
                  {o.signedIn} signed in · {o.devices} devices · {o.admins} admins · {o.settings.headcountInScope} in scope
                </p>
              </div>
              {pending?.id === o.id ? (
                <div className="org-confirm" role="alertdialog" aria-label={confirmCopy(o, pending.status)}>
                  <span>{confirmCopy(o, pending.status)}</span>
                  <button type="button" className="bc-btn" onClick={() => void setStatus(o, pending.status)}>
                    Confirm
                  </button>
                  <button type="button" className="bc-btn bc-btn-quiet" onClick={() => setPending(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="org-actions">
                  <button type="button" className="bc-btn" onClick={() => onOpen({ id: o.id, name: o.name })}>
                    Open
                  </button>
                  {o.status === "pilot" && (
                    <button type="button" className="bc-btn bc-btn-quiet" onClick={() => void setStatus(o, "active")}>
                      Activate
                    </button>
                  )}
                  {o.status === "paused" && (
                    <button type="button" className="bc-btn bc-btn-quiet" onClick={() => void setStatus(o, "active")}>
                      Resume
                    </button>
                  )}
                  {(o.status === "pilot" || o.status === "active") && (
                    <button type="button" className="bc-btn bc-btn-quiet" onClick={() => setPending({ id: o.id, status: "paused" })}>
                      Pause
                    </button>
                  )}
                  {o.status !== "archived" && (
                    <button type="button" className="bc-btn bc-btn-quiet" onClick={() => setPending({ id: o.id, status: "archived" })}>
                      Archive
                    </button>
                  )}
                  {o.status === "archived" && (
                    <button type="button" className="bc-btn bc-btn-quiet" onClick={() => setPending({ id: o.id, status: "active" })}>
                      Reopen
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        <p className="bc-note">
          Pilot and Active behave the same. Paused stops prompts and keeps sign-in open. Archived closes sign-in and
          stops prompts; people keep access to their own data.
        </p>
      </section>

      <form className="bc-block" onSubmit={(e) => void create(e)}>
        <div className="bc-block-head">
          <h2>Provision an organisation</h2>
        </div>
        <div className="set-row">
          <span className="set-label">Name</span>
          <span className="set-control">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">
            Email domains
            <span className="set-sub">Anyone with a verified address on these joins on first sign-in.</span>
          </span>
          <span className="set-control">
            <input required placeholder="example.co.uk, example.com" value={form.domains} onChange={(e) => setForm({ ...form, domains: e.target.value })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">Timezone</span>
          <span className="set-control">
            <input required value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">People in scope</span>
          <span className="set-control">
            <input type="number" min={0} value={form.headcountInScope} onChange={(e) => setForm({ ...form, headcountInScope: Number(e.target.value) })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">
            Sponsor's email
            <span className="set-sub">Invited as the first org admin.</span>
          </span>
          <span className="set-control">
            <input type="email" required value={form.sponsorEmail} onChange={(e) => setForm({ ...form, sponsorEmail: e.target.value })} />
          </span>
        </div>
        <div className="set-actions">
          <button type="submit" className="bc-btn">
            Create
          </button>
          {note && <span className="set-state">{note}</span>}
        </div>
      </form>
    </div>
  );
}

export function ReviewsScreen() {
  const api = useApi();
  const queue = useResource(async (a) => unwrap(await a.GET("/v1/platform/dse-reviews")));
  const [advice, setAdvice] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const item: DseReviewItem | undefined = queue.data?.[0];

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!item) return;
    const r = await api.POST("/v1/platform/dse-reviews/{dseId}", { params: { path: { dseId: item.id } }, body: { advice } });
    if (r.data) {
      setDone(`Advice sent for the ${item.orgName} check. It is on the person's own workstation screen now.`);
      setAdvice("");
      queue.reload();
    }
  }

  return (
    <div className="bc-screen bc-settings">
      <header className="bc-head">
        <p className="bc-kicker">Platform</p>
        <h1>Workstation reviews</h1>
        <p className="bc-sub">
          {queue.data ? `${queue.data.length} waiting` : "Loading"}. One at a time, oldest first. Nothing here names a person, and the employer never sees an
          individual result.
        </p>
      </header>
      {done && <p className="bc-note">{done}</p>}
      {item && (
        <form className="bc-block" onSubmit={(e) => void send(e)}>
          <div className="bc-block-head">
            <h2>{item.orgName}</h2>
            <span className="set-sub">
              {new Date(item.completedAt).toLocaleDateString()} · {item.workLocation.replace(/_/g, " ")} · {item.scoreOverall} out of 100
            </span>
            <RagBadge rag={item.headlineRag} />
          </div>
          <ul className="notreported">
            {item.priority.map((c) => (
              <li key={c.key}>
                <RagBadge rag={c.rag} /> <b>{c.label}</b> {c.score} out of 100 <span className="set-sub">{c.action}</span>
              </li>
            ))}
          </ul>
          {item.flagged.length > 0 && <p className="bc-note">Scored the maximum: {item.flagged.join(", ")}</p>}
          <label className="set-row">
            <span className="set-label">
              Advice to the person
              <span className="set-sub">Plain words. It appears on their workstation screen with a nudge to raise it with their manager.</span>
            </span>
            <span className="set-control">
              <textarea required rows={5} value={advice} onChange={(e) => setAdvice(e.target.value)} />
            </span>
          </label>
          <div className="set-actions">
            <button type="submit" className="bc-btn">
              Send advice
            </button>
          </div>
        </form>
      )}
      {queue.data && queue.data.length === 0 && <p className="bc-note">Nothing waiting.</p>}
    </div>
  );
}

export function PoliciesScreen() {
  const policies = useResource(async (a) => unwrap(await a.GET("/v1/platform/policies")));
  return (
    <div className="bc-screen bc-settings">
      <header className="bc-head">
        <p className="bc-kicker">Platform</p>
        <h1>Prompt policies</h1>
        <p className="bc-sub">Versioned data. A new version is a pull request; an org picks one in its settings and the change is audited.</p>
      </header>
      <section className="bc-block">
        <ul className="notreported">
          {(policies.data ?? []).map((p) => (
            <li key={p.version}>
              <b>{p.version}</b> {p.summary} <span className="set-sub">{p.orgs.length ? `used by ${p.orgs.join(", ")}` : "not in use"}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
